import {
  AppConfigConstantsType,
  TaxCalendarConfigRecordType,
} from '@nxsys/types/models/apps/appConfigs';
import { APP_CONFIGS } from '@nxsys/store/constants/keys';

class AppConfigManager {
  private static instance: AppConfigManager;
  private configs: AppConfigConstantsType | null = null;
  private isInitialized: boolean = false;

  private constructor() {
    this.loadFromLocalStorage();
  }

  public static getInstance(): AppConfigManager {
    if (!AppConfigManager.instance) {
      AppConfigManager.instance = new AppConfigManager();
    }
    return AppConfigManager.instance;
  }

  private loadFromLocalStorage(): void {
    try {
      const storedConfigs = localStorage.getItem(APP_CONFIGS);
      if (storedConfigs) {
        this.configs = JSON.parse(storedConfigs) as AppConfigConstantsType;
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('Failed to load app configs from localStorage:', error);
      this.configs = null;
      this.isInitialized = false;
    }
  }

  private saveToLocalStorage(configs: AppConfigConstantsType): void {
    try {
      localStorage.setItem(APP_CONFIGS, JSON.stringify(configs));
    } catch (error) {
      console.error('Failed to save app configs to localStorage:', error);
    }
  }

  /**
   * Get current app configurations
   * @returns AppConfigConstantsType | null
   */
  public getConfigs(): AppConfigConstantsType | null {
    return this.configs;
  }

  /**
   * Get specific configuration by key
   * @param key - Key of the configuration to retrieve
   * @returns Configuration value or undefined
   */
  public getConfig<K extends keyof AppConfigConstantsType>(
    key: K,
  ): AppConfigConstantsType[K] | undefined {
    return this.configs?.[key];
  }

  public getTaxCalendar(): TaxCalendarConfigRecordType | null {
    return this.configs?.taxCalendar || null;
  }

  /**
   * Set app configurations (internal use only)
   * This method should only be called by the Redux action
   * @param configs - New configuration data
   */
  public setConfigs(configs: AppConfigConstantsType): void {
    this.configs = configs;
    this.isInitialized = true;
    this.saveToLocalStorage(configs);
  }

  /**
   * Check if configs are initialized
   */
  public isConfigsInitialized(): boolean {
    return this.isInitialized && this.configs !== null;
  }

  /**
   * Get configs if available, returns null if not initialized
   * Use Redux action getAppConfigs to fetch from backend
   * @returns Current configs or null if not initialized
   */
  public getConfigsOrNull(): AppConfigConstantsType | null {
    return this.isConfigsInitialized() ? this.configs : null;
  }

  public clearConfigs(): void {
    this.configs = null;
    this.isInitialized = false;
    try {
      localStorage.removeItem(APP_CONFIGS);
    } catch (error) {
      console.error('Failed to clear app configs from localStorage:', error);
    }
  }
}

// Export singleton instance
export const appConfigManager = AppConfigManager.getInstance();

// Export convenience methods for direct access
export const getAppConfigs = (): AppConfigConstantsType | null => {
  return appConfigManager.getConfigs();
};

export const getAppConfig = <K extends keyof AppConfigConstantsType>(
  key: K,
): AppConfigConstantsType[K] | undefined => {
  return appConfigManager.getConfig(key);
};

export const isAppConfigsInitialized = (): boolean => {
  return appConfigManager.isConfigsInitialized();
};
